import { EventType } from "@/analytics/analytics-events";
import { dismissBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { CreateProjectSnapshotTask } from "@/utils/background-tasks";
import { TranslateVar, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
} from "@faro-lotv/service-wires";
import { useCallback, useMemo, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import { getTaskStatusLabel } from "../task-descriptions";

type SnapshotTaskCardProps = {
  /** The create snapshot task */
  task: CreateProjectSnapshotTask;
};

/** @returns The card for the create snapshot task */
export function SnapshotTaskCard({ task }: SnapshotTaskCardProps): JSX.Element {
  const dispatch = useAppDispatch();

  const { openToast } = useToast();

  const copySnapshotLink = useCallback(() => {
    Analytics.track(EventType.openSnapshotLink);

    const { snapshotId } = task.metadata;
    navigator.clipboard.writeText(
      `${window.location.origin}/project/${snapshotId}`,
    );
    openToast({ title: "Link copied to clipboard" });
  }, [openToast, task.metadata]);

  const dismissErrorTask = useCallback((): void => {
    dispatch(dismissBackgroundTask(task.id));
  }, [dispatch, task.id]);

  const actions = useMemo<CardAction | undefined>(() => {
    if (task.state === BackgroundTaskState.failed) {
      return { name: "Clear", callback: dismissErrorTask };
    }
    if (task.metadata.snapshotId) {
      return {
        name: "Copy link",
        callback: copySnapshotLink,
      };
    }
  }, [
    dismissErrorTask,
    copySnapshotLink,
    task.metadata.snapshotId,
    task.state,
  ]);

  return (
    <GenericCardLayout
      name={
        <TranslateVar name="snapshotProjectName">
          {task.metadata.snapshotName ?? "Snapshot"}
        </TranslateVar>
      }
      subText="Snapshots"
      startTime={task.createdAt}
      action={actions}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress label={getTaskStatusLabel(task)} task={task} />
      )}
      {task.state === BackgroundTaskState.failed && <CardError task={task} />}
    </GenericCardLayout>
  );
}
